import {
  type TzMode,
  formatDurationSeconds,
  prettyEnglishDateTimeTz,
  prettyEnglishDateTimeTzFull,
} from "@app/date";
import {
  selectEnvironmentById,
  selectResourceByIdAndType,
  selectStackById,
} from "@app/deploy";
import {
  type FreezerActivity,
  extractOperationIdFromActivityForLogs,
  findTargetResourceOfType,
  formatActivityType,
  getTargetResourceUrlForActivity,
  selectFreezerFilterAccountsWithStatus,
} from "@app/freezer";
import { useSelector } from "@app/react";
import { activityDetailUrl, environmentDetailUrl } from "@app/routes";
import { capitalize } from "@app/string-utils";
import type { OperationStatus, ResourceType } from "@app/types";
import { createColumnHelper } from "@tanstack/react-table";
import cn from "classnames";
import { Link } from "react-router-dom";
import { CopyTextButton } from "../copy";
import { IconChevronRight, IconXCircle } from "../icons";
import { OpStatus } from "../operation-status";
import { getIconForResourceType } from "../resource-icons";
import { tokens } from "../tokens";
import { Tooltip } from "../tooltip";
import { useTruncationDetection } from "./use-truncation-detection";

export interface ActivityTableMeta {
  tz: TzMode;
  q?: string;
}

const columnHelper = createColumnHelper<FreezerActivity>();

const highlightMatch = (text: string, query: string) => {
  if (!query || !text) return text;

  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);

  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark
        key={i}
        style={{
          backgroundColor: "#FEF08A",
          padding: "0",
          margin: "0",
        }}
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

function OperationCellContent({
  activity,
  query,
}: { activity: FreezerActivity; query?: string }) {
  const operationId = extractOperationIdFromActivityForLogs(activity);
  const idPrefix = operationId ? "Operation:" : "Activity:";
  const idValue = operationId ?? activity.id;
  const linkUrl = activityDetailUrl(activity.id);
  const titleText = activity.title || formatActivityType(activity.activityType);
  const { ref: titleRef, isTruncated: isTitleTruncated } =
    useTruncationDetection("width");
  const { ref: idRef, isTruncated: isIdTruncated } =
    useTruncationDetection("width");

  return (
    <div className="min-w-0">
      <div className="relative group/opTitle min-w-0">
        <Link to={linkUrl} className={cn(tokens.type["table link"], "block")}>
          <div ref={titleRef} className="truncate">
            {titleText}
          </div>
        </Link>
        {isTitleTruncated && (
          <div className="hidden group-hover/opTitle:block absolute bottom-full left-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
            {highlightMatch(titleText, query || "")}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 text-gray-500 text-sm group/opid min-w-0">
        <div className="relative group/opIdText min-w-0">
          <div
            ref={idRef}
            className="truncate border-b border-dotted border-gray-400 group-hover/opid:text-indigo"
          >
            <span>{idPrefix} </span>
            <span>{highlightMatch(idValue, query || "")}</span>
          </div>
          {isIdTruncated && (
            <div className="hidden group-hover/opIdText:block absolute bottom-full left-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
              {idPrefix} {highlightMatch(idValue, query || "")}
            </div>
          )}
        </div>
        <span className="invisible group-hover/opid:visible inline-flex shrink-0">
          <CopyTextButton text={idValue} />
        </span>
      </div>
    </div>
  );
}

function SummaryCellContent({
  activity,
  query,
}: { activity: FreezerActivity; query?: string }) {
  const { ref: textRef, isTruncated } = useTruncationDetection("width");

  if (!activity.summary) return null;

  return (
    <div className="relative group/summary min-w-0">
      <div ref={textRef} className="text-sm text-gray-700 truncate">
        {highlightMatch(activity.summary, query || "")}
      </div>
      {isTruncated && (
        <div className="hidden group-hover/summary:block absolute bottom-full left-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
          {highlightMatch(activity.summary, query || "")}
        </div>
      )}
    </div>
  );
}

export function TargetCellContent({
  activity,
  query,
}: { activity: FreezerActivity; query?: string }) {
  const { ref: nameRef, isTruncated: isNameTruncated } =
    useTruncationDetection("width");
  const { ref: typeRef, isTruncated: isTypeTruncated } =
    useTruncationDetection("width");

  const resourceFound = useSelector((s) =>
    selectResourceByIdAndType(s, {
      resourceId: activity.primaryResourceId,
      resourceType: activity.primaryResourceType,
    }),
  );

  let displayText =
    activity?.targetResources?.find(
      (a) => a?.id === activity?.primaryResourceId,
    )?.identifier || "";

  if (activity.activityType === "database.tunnel") {
    const dbResource = findTargetResourceOfType(
      activity.targetResources || [],
      "database",
    );
    if (dbResource) {
      displayText = dbResource.identifier;
    }
  }

  const targetUrl = getTargetResourceUrlForActivity(activity);

  let resourceType = activity.primaryResourceType.toLocaleLowerCase();
  if (resourceType === "account") {
    resourceType = "environment";
  }

  const resourceDeleted =
    resourceFound !== null &&
    "deletedAt" in resourceFound &&
    resourceFound?.deletedAt;
  const resourceAvailable = !!resourceFound && !resourceDeleted;
  const displayLink =
    resourceAvailable ||
    // these below resources will always have links, even when deleted.
    ["app", "database", "endpoint"].includes(resourceType.toLocaleLowerCase());

  return (
    <div className="flex min-w-0">
      <div className="mr-2 pt-1 shrink-0">
        {getIconForResourceType(resourceType)}
      </div>
      <div className="min-w-0">
        <div className="relative group/targetName min-w-0">
          {displayLink && targetUrl ? (
            <Link
              to={targetUrl}
              className={cn(tokens.type["table link"], "block")}
            >
              <div ref={nameRef} className="text-sm whitespace-nowrap truncate">
                {highlightMatch(displayText, query || "")}
              </div>
            </Link>
          ) : (
            <div className={tokens.type["table lead text"]}>
              <div ref={nameRef} className="text-sm whitespace-nowrap truncate">
                {highlightMatch(displayText, query || "")}
              </div>
            </div>
          )}
          {isNameTruncated && (
            <div className="hidden group-hover/targetName:block absolute bottom-full left-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
              {highlightMatch(displayText, query || "")}
            </div>
          )}
        </div>
        <div className="relative group/targetType min-w-0">
          <div ref={typeRef} className="text-gray-500 truncate">
            {highlightMatch(
              capitalizeResourceType(
                resourceType,
                activity.primaryResourceType,
              ),
              query || "",
            )}
          </div>
          {isTypeTruncated && (
            <div className="hidden group-hover/targetType:block absolute bottom-full left-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
              {highlightMatch(
                capitalizeResourceType(
                  resourceType,
                  activity.primaryResourceType,
                ),
                query || "",
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function capitalizeResourceType(
  resourceTypeLowerCase: ResourceType | string,
  resourceTypeOrigin: string,
): string {
  switch (resourceTypeLowerCase) {
    case "activityexport":
    case "externalawsaccount":
    case "externalawsresource":
    case "llmkey":
    case "logdrain":
    case "metricdrain":
    case "service_sizing_policy":
    case "service_sizing_policy_membership":
    case "servicesizingpolicy":
    case "servicesizingpolicymembership":
    case "vpntunnel":
      return resourceTypeOrigin;
    case "persistentdisk":
      return "Persistent Disk";
    case "diskattachment":
      return "Disk Attachment";
    default:
      return capitalize(resourceTypeLowerCase);
  }
}

function EnvCellContent({ activity }: { activity: FreezerActivity }) {
  const acctId = activity.accountId || "";
  const env = useSelector((s) => selectEnvironmentById(s, { id: acctId }));
  const stack = useSelector((s) => selectStackById(s, { id: env.stackId }));
  const freezerAccounts = useSelector(selectFreezerFilterAccountsWithStatus);
  const { ref: handleRef, isTruncated: isHandleTruncated } =
    useTruncationDetection("width");
  const { ref: stackRef, isTruncated: isStackTruncated } =
    useTruncationDetection("width");

  const freezerAccount = freezerAccounts.find(
    (a) => a.value === activity.accountId,
  );
  const isDeleted = activity.accountId && freezerAccount?.deleted === true;

  if (!activity.accountId) {
    return (
      <div className="flex items-center gap-1">
        <div className="w-4 shrink-0" />
        <span className="text-gray-500">None</span>
      </div>
    );
  }

  if (isDeleted) {
    let handle: string;
    if (env.id) {
      handle = env.handle;
    } else {
      handle = freezerAccount?.title || `Environment ${activity.accountId}`;
    }
    return (
      <Tooltip text="This environment has been deleted">
        <div className="flex items-center gap-1 cursor-default">
          <div className="w-4 shrink-0">
            <IconXCircle
              variant="sm"
              title="This environment has been deleted"
              className="stroke-black fill-red-300"
            />
          </div>
          <span>{handle}</span>
        </div>
      </Tooltip>
    );
  }

  const stackText = `${stack.organizationId ? "Dedicated Stack " : "Shared Stack "}(${stack.region})`;

  return (
    <div className="flex items-center gap-1">
      <div className="min-w-0">
        <div className="relative group/envHandle min-w-0">
          <Link
            to={environmentDetailUrl(env.id)}
            className={cn(tokens.type["table link"], "block")}
          >
            <div ref={handleRef} className="truncate">
              {env.handle}
            </div>
          </Link>
          {isHandleTruncated && (
            <div className="hidden group-hover/envHandle:block absolute bottom-full right-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
              {env.handle}
            </div>
          )}
        </div>
        <div className="relative group/envStack">
          <div
            ref={stackRef}
            className={cn(tokens.type["normal lighter"], "truncate")}
          >
            {stackText}
          </div>
          {isStackTruncated && (
            <div className="hidden group-hover/envStack:block absolute bottom-full right-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
              {stackText}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OccurredAtCellContent({
  activity,
  tz,
}: { activity: FreezerActivity; tz: TzMode }) {
  const timestamp = prettyEnglishDateTimeTz(activity.occurredAt, tz);
  const inverseTz: TzMode = tz === "utc" ? "local" : "utc";
  const tooltipText = prettyEnglishDateTimeTzFull(
    activity.occurredAt,
    inverseTz,
  );
  const durationSeconds =
    typeof activity.description?.duration_seconds === "number"
      ? activity.description.duration_seconds
      : null;

  return (
    <div className="relative group/tz">
      <div className="flex flex-col cursor-default">
        <div className={tokens.type["table lead text"]}>{timestamp}</div>
        {durationSeconds !== null ? (
          <div className="text-xs text-gray-500">
            Duration: {formatDurationSeconds(durationSeconds)}
          </div>
        ) : null}
      </div>
      <div className="hidden group-hover/tz:block absolute bottom-full right-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-nowrap z-50">
        {tooltipText}
      </div>
    </div>
  );
}

function ActorCellContent({ activity }: { activity: FreezerActivity }) {
  const { ref: nameRef, isTruncated: isNameTruncated } =
    useTruncationDetection("width");
  const { ref: emailRef, isTruncated: isEmailTruncated } =
    useTruncationDetection("width");

  return (
    <div className="min-w-0">
      <div className="relative group/actorName">
        <div
          ref={nameRef}
          className={cn(tokens.type["table lead text"], "truncate")}
        >
          {activity.userName || "System"}
        </div>
        {isNameTruncated && (
          <div className="hidden group-hover/actorName:block absolute bottom-full left-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
            {activity.userName || "System"}
          </div>
        )}
      </div>
      {activity.userEmail ? (
        <div className="relative group/actorEmail">
          <div
            ref={emailRef}
            className="text-gray-500 whitespace-nowrap truncate"
          >
            {activity.userEmail}
          </div>
          {isEmailTruncated && (
            <div className="hidden group-hover/actorEmail:block absolute bottom-full left-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
              {activity.userEmail}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export const ACTIVITY_COLUMNS = [
  columnHelper.display({
    id: "operation",
    header: "Operation",
    cell: (info) => (
      <OperationCellContent
        activity={info.row.original}
        query={(info.table.options.meta as ActivityTableMeta)?.q}
      />
    ),
    size: 200,
    minSize: 40,
    maxSize: 2000,
  }),

  columnHelper.display({
    id: "target",
    header: "Target",
    cell: (info) => (
      <TargetCellContent
        activity={info.row.original}
        query={(info.table.options.meta as ActivityTableMeta)?.q}
      />
    ),
    size: 200,
    minSize: 60,
    maxSize: 2000,
  }),

  columnHelper.display({
    id: "additionalInfo",
    header: "Additional Info",
    cell: (info) => (
      <SummaryCellContent
        activity={info.row.original}
        query={(info.table.options.meta as ActivityTableMeta)?.q}
      />
    ),
    size: 250,
    minSize: 40,
    maxSize: 2000,
  }),

  columnHelper.display({
    id: "status",
    header: "Status",
    cell: (info) => {
      const activity = info.row.original;
      return <OpStatus status={activity.status as OperationStatus} />;
    },
    size: 120,
    minSize: 105,
    maxSize: 2000,
  }),

  columnHelper.display({
    id: "actor",
    header: "Actor",
    cell: (info) => <ActorCellContent activity={info.row.original} />,
    size: 180,
    minSize: 40,
    maxSize: 2000,
  }),

  columnHelper.display({
    id: "environment",
    header: "Environment",
    cell: (info) => <EnvCellContent activity={info.row.original} />,
    size: 180,
    minSize: 56,
    maxSize: 2000,
  }),

  columnHelper.display({
    id: "occurredAt",
    header: (info) => {
      const meta = info.table.options.meta as ActivityTableMeta;
      return `Occurred At (${meta.tz === "utc" ? "UTC" : "Local"})`;
    },
    cell: (info) => {
      const meta = info.table.options.meta as ActivityTableMeta;
      return (
        <OccurredAtCellContent activity={info.row.original} tz={meta.tz} />
      );
    },
    size: 180,
    minSize: 40,
    maxSize: 2000,
  }),

  columnHelper.display({
    id: "details",
    header: "",
    cell: (info) => {
      const activity = info.row.original;
      const linkUrl = `/activity/${activity.id}`;

      return (
        <div className="relative group/activityDetail">
          <Link
            to={linkUrl}
            className="flex justify-center items-center p-1 hover:bg-gray-200 rounded mr-3"
          >
            <IconChevronRight className="w-5 h-5 stroke-black group-hover:stroke-indigo hover:stroke-indigo" />
          </Link>
          <div className="hidden group-hover/activityDetail:block absolute bottom-full right-0 mb-1 px-2 py-1 bg-black/85 text-white text-sm rounded whitespace-normal z-50 max-w-sm">
            Details
          </div>
        </div>
      );
    },
    size: 56,
    minSize: 40,
    enableResizing: false,
  }),
];

export const TOGGLEABLE_COLUMNS = [
  { id: "operation", label: "Operation", disabled: true },
  { id: "target", label: "Target" },
  { id: "additionalInfo", label: "Additional Info" },
  { id: "status", label: "Status" },
  { id: "actor", label: "Actor" },
  { id: "environment", label: "Environment" },
  { id: "occurredAt", label: "Occurred At" },
  { id: "details", label: "Details", hidden: true },
];

export const DEFAULT_COLUMN_ORDER = [
  "operation",
  "target",
  "additionalInfo",
  "status",
  "actor",
  "environment",
  "occurredAt",
  "details",
];

export const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  operation: true,
  target: true,
  additionalInfo: true,
  status: true,
  actor: true,
  environment: true,
  occurredAt: true,
  details: true,
};
